import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { BrowseResultType } from 'fym-shared/src/music24/browseSearch';
import { TypedMemo } from '../../../common/types';
import type { BrowseSearchData } from '../hooks/useBrowseSearch';
import { browseMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseSearch.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseTypeSwitchProps {
  value: BrowseResultType;
  onChange: (type: BrowseResultType) => void;
  /** The counts of the last answer; "…" while the first answer loads (design b3). */
  data: BrowseSearchData | undefined;
}

/** All / Artists / Tracks / Playlists with counts (design a1). "Lists" on a phone (design c1). */
const BrowseTypeSwitchComponent: React.FC<BrowseTypeSwitchProps> = ({ value, onChange, data }) => {
  const intl = useIntl();
  const count = (type: BrowseResultType): string => {
    if (data === undefined) {
      return '…';
    }
    const capped =
      (type === 'artists' && data.countsCapped.artists) ||
      (type === 'tracks' && data.countsCapped.tracks) ||
      (type === 'playlists' && data.countsCapped.playlists);
    return `${data.counts[type]}${capped ? '+' : ''}`;
  };
  const options: { type: BrowseResultType; label: string; short?: string }[] = [
    { type: 'all', label: intl.formatMessage(messages.typeAll) },
    { type: 'artists', label: intl.formatMessage(messages.typeArtists) },
    { type: 'tracks', label: intl.formatMessage(messages.typeTracks) },
    {
      type: 'playlists',
      label: intl.formatMessage(messages.typePlaylists),
      short: intl.formatMessage(messages.typePlaylistsShort),
    },
  ];

  return (
    <div className={styles.types} role="tablist" aria-label={intl.formatMessage(messages.typeSwitch)}>
      {options.map((option) => (
        <button
          key={option.type}
          type="button"
          role="tab"
          aria-selected={option.type === value}
          className={clsx(styles.typeButton, option.type === value && styles.typeOn)}
          onClick={() => onChange(option.type)}
          data-testid={`browse-type-${option.type}`}
        >
          {option.short === undefined ? (
            <span>{option.label}</span>
          ) : (
            <>
              <span className={styles.labelLong}>{option.label}</span>
              <span className={styles.labelShort}>{option.short}</span>
            </>
          )}
          <span className={styles.typeCount}>{count(option.type)}</span>
        </button>
      ))}
    </div>
  );
};

export const BrowseTypeSwitch = TypedMemo(BrowseTypeSwitchComponent);
